import { call } from "./tasks";
import type { Issue } from "./tasks";

/* ══ 서버(나루)별 서비스 — `GET /servers` · 리포 추가는 `POST /tasks`(type=ops, cmd=append) ══
   `crates/api/src/servers.rs` · `docs/decisions/servers.md`. 바로가기 주소는 서버가 만든다(`links`) —
   화면이 「어느 포트가 웹인가」를 다시 판정하지 않는다. */

export type ServiceLink = { kind: "domain" | "ip"; url: string; label: string };
export type ServicePort = { key: string; value: string };

/** 서버의 svc-hosts 가 보고한 서비스 한 줄 + 서버가 붙인 `links`. */
export type Service = {
  name: string;
  repo: string;
  branch: string;
  dir: string;
  /** 포트를 어디서 읽었나 — `env`(서비스 .env 실제 값) · `list`(세팅 전, 목록의 PORT) · `none`(안 엶). */
  from: "env" | "list" | "none";
  ports: ServicePort[];
  primary?: string | null;
  domain: string;
  state: "running" | "stopped" | "not-synced";
  links: ServiceLink[];
};

export type Server = {
  /** 러너(토큰) 이름 — 신원이다. */
  runner: string;
  /** 지금 붙은 WS 수. 0 이면 「안 붙음」. */
  connections: number;
  /** 붙은 방식(`host` · `docker`). 안 붙었거나 옛 나루면 null(「모름」). */
  mode?: string | null;
  /** 끊긴 러너만 — 끊긴 시각(러너 표 · 종과 같은 판정). */
  downSince?: string | null;
  /** 보고가 없으면 셋 다 null — 「모름」이다(서비스 0개와 다르다). */
  host: string | null;
  ip: string | null;
  reportedAt: string | null;
  services: Service[] | null;
};

export type AppendSpec = { host: string; repo: string; branch: string; service: string; port: string };

export const serversApi = {
  list: () => call<{ servers: Server[] }>(`/servers`).then((r) => r.servers),
  /** 웹 「리포 추가」 — 서버 제어 이슈를 **대기로** 세운다. 사람이 누른 것이 실행 승인이다
   *  (보드에서 「대기」로 올리는 걸음과 같은 뜻, `docs/decisions/ops-task.md`). */
  append: (s: AppendSpec, title: string, intent: string) =>
    call<Issue>(`/tasks`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        title,
        intent,
        type: "ops",
        status: "ready",
        ops: {
          host: s.host,
          cmd: "append",
          repo: s.repo,
          branch: s.branch || undefined,
          service: s.service || undefined,
          port: s.port || undefined,
        },
      }),
    }),
};

/** 리포 주소를 `owner/이름`(소문자)으로 맞춘다 — ssh · https · 이름만 준 것이 같은 리포인지 견주려는 것.
 *  svc-hosts 의 `repo_key` 와 같은 규칙이다(이름만이면 irolab-co). */
export function repoKey(repo: string): string {
  const s = repo
    .trim()
    .replace(/\.git$/, "")
    .replace(/^(https?|ssh):\/\/([^@/]*@)?[^/]*\//, "")
    .replace(/^[^@/]*@[^:]*:/, "");
  return (s.includes("/") ? s : `irolab-co/${s}`).toLowerCase();
}

/** 이 서버 몫의 「리포 추가」 이슈(최근 것부터). 이슈 목록에서 고른다 — 따로 묻는 문을 안 만든다. */
export function appendTasks(tasks: Issue[], host: string): Issue[] {
  return tasks
    .filter((t) => t.type === "ops" && t.ops?.cmd === "append" && t.ops.host === host)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
